import { type FC, useState, useEffect, useRef, useMemo } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { HeadingSize, PageHeading } from "@via/components/page-heading.tsx";
import { useDeepCompareEffect } from "@via/hooks/useDeepCompare.tsx";
import {
  type InputNode,
  getProcessInputArrayIndices,
  parseDropdownOptions,
  shouldDisplayInput,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { classNames, getDirtyFieldPaths } from "@via/utils/helpers.tsx";
import { type UseFormReturn, useWatch } from "react-hook-form";

interface HorizontalInputsArrayProps {
  form: UseFormReturn;
  processOptions?: Record<string, any>;
  inputs: InputNode[];
  style: Record<string, any>[];
  mainScriptHeaderConfig: string;
  renderInputOptions: (
    input: InputNode,
    index: number,
    style?: Record<string, any>[]
  ) => JSX.Element;
  isArray?: boolean;
  readOnly?: boolean;
}

const HorizontalInputsArray: FC<HorizontalInputsArrayProps> = ({
  form,
  processOptions,
  inputs,
  style,
  mainScriptHeaderConfig,
  renderInputOptions,
  isArray,
  readOnly,
}) => {
  const prevValuesRef = useRef<Record<string, any>>({});

  const { control, setValue, unregister, watch } = form;
  const [arrayIndices, setArrayIndices] = useState<Record<string, any>[]>([]);
  const [relatedAutoformFields, setRelatedAutoformFields] = useState<string[]>(
    []
  );

  const { dirtyFields } = form.formState;

  const dirtyFieldPaths = useMemo(
    () => getDirtyFieldPaths(dirtyFields),
    [dirtyFields]
  );

  useDeepCompareEffect(() => {
    if (!isArray || !inputs?.length) return;

    const processId = inputs[0]?.id?.split(".")[0];
    const inputIds = inputs.map(({ inputId }) => inputId);
    const collectedIndices = new Set<string>();

    // 1. From processOptions
    if (processOptions?.[processId]) {
      const initialArrayIndices = getProcessInputArrayIndices(
        processOptions[processId],
        inputIds
      );
      initialArrayIndices.forEach(indice => collectedIndices.add(indice));
    }

    // 2. Supplement with form values
    const formValues = form.getValues();
    const processValues = formValues?.[processId];
    if (processValues) {
      Object.keys(processValues).forEach(fieldKey => {
        inputIds.forEach(inputId => {
          const match = fieldKey.match(new RegExp(`^${inputId}(_ind\\d+)$`));
          if (match) {
            collectedIndices.add(match[1]); // e.g. "_ind1"
          }
        });
      });
    }

    // 3. Fallback to defVal only if processOptions is missing
    if (!processOptions?.[processId]) {
      inputs.forEach(({ id, inputDetails }) => {
        const { defVal } = inputDetails;
        if (Array.isArray(defVal)) {
          defVal.forEach((val, index) => {
            const newIndice = `_ind${index + 1}`;
            if (!collectedIndices.has(newIndice)) {
              collectedIndices.add(newIndice);
            }
            setValue(`${id}${newIndice}`, val);
          });
        }
      });
    }

    // 4. Apply combined unique indices
    const mergedArrayIndices = Array.from(collectedIndices).map(indice => ({
      inputs,
      indice,
    }));
    setArrayIndices(mergedArrayIndices);
  }, [isArray, inputs, processOptions]);

  // Effect to update relatedAutoformFields after setting arrayIndices
  useEffect(() => {
    if (arrayIndices.length > 0) {
      const updatedAutoformFields = new Set<string>();

      arrayIndices.forEach(({ inputs, indice }) => {
        inputs.forEach(input => {
          if (input.inputDetails?.autoform) {
            input.inputDetails.autoform.forEach(({ condition }) => {
              const fieldName = `${input.id.split(".")[0]}.${condition.split("=")[0].trim()}${indice}`;
              updatedAutoformFields.add(fieldName);
            });
          }
        });
      });

      setRelatedAutoformFields(Array.from(updatedAutoformFields));
    }
  }, [arrayIndices]);

  // Watch all related fields for autoform input changes
  const watchedAutoformValues = useWatch({
    name: relatedAutoformFields,
    control,
  });

  useEffect(() => {
    if (!inputs.length || !isArray) return;

    const processId = inputs[0]?.id?.split(".")?.[0];
    const autoformInputs = inputs.filter(input => input.inputDetails.autoform);

    autoformInputs.forEach(input => {
      // Get corresponding indices for the input
      const inputIndices = arrayIndices
        .filter(({ inputs }) => inputs.some(({ id }) => id === input.id))
        .map(({ indice }) => indice);

      // If no indices are available, fall back to an empty string
      (inputIndices.length > 0 ? inputIndices : [""]).forEach(indice => {
        // Skip autoform if already defined in processOptions
        if (
          processOptions?.[processId]?.[`${input.inputId}${indice}`] !==
          undefined
        ) {
          return;
        }
        input.inputDetails.autoform?.forEach(({ condition, value }) => {
          const [condId, values] = condition.split("=");
          const valueList = values.replace(/[()]/g, "").split("|");

          // Watch condition value from the form
          const actualFormValue = watch(
            `${input.id.split(".")[0]}.${condId}${indice}`
          );

          // Search condition in the main script header config
          const searchPattern = new RegExp(
            `^\\s*${condId}\\s*=\\s*['"]([^'"]+)['"]`
          );
          const matchedInScriptHeaderConfig =
            mainScriptHeaderConfig.match(searchPattern);

          if (
            (matchedInScriptHeaderConfig &&
              valueList.includes(matchedInScriptHeaderConfig[1])) ||
            valueList.includes(actualFormValue)
          ) {
            // Search the value in the form inputs
            const valueFromInput = watch(
              `${input.id.split(".")[0]}.${value}${indice}`
            );
            const prevValue =
              prevValuesRef.current[`${input.id}.${condId}${indice}`];

            // Update only if the new value is different from the previous value
            if (prevValue !== actualFormValue) {
              const targetInputKey = `${input.id}${indice}`;
              // Skip if the input is already dirty
              if (dirtyFieldPaths.has(targetInputKey)) return;

              setValue(`${input.id}${indice}`, valueFromInput || value, {
                shouldDirty: false,
              });
              prevValuesRef.current[`${input.id}.${condId}${indice}`] =
                actualFormValue;
            }
          }
        });
      });
    });
  }, [mainScriptHeaderConfig, watchedAutoformValues, arrayIndices]);

  const title = inputs.find(({ inputDetails }) => inputDetails?.title)
    ?.inputDetails?.title;

  const generateNextIndice = () => {
    // Start with _ind1 or increment the last _ind number safely
    const currentIndices = arrayIndices || [];
    if (currentIndices.length === 0) {
      return "_ind1";
    }

    const lastIndice = currentIndices[currentIndices.length - 1]?.indice;

    // Safely extract and increment the number part of the last index
    const lastNumber = lastIndice?.match(/_ind(\d+)$/)?.[1];
    const nextNumber = lastNumber ? parseInt(lastNumber, 10) + 1 : 1;

    return `_ind${nextNumber}`;
  };

  const handleAddInput = () => {
    // Start with _ind1 or increment the last _ind number:
    const nextIndice = generateNextIndice();

    // Check if there are no existing inputs and if defVal is an array
    let newArrayIndices: Record<string, any>[] = [];
    if (arrayIndices.length === 0) {
      inputs.forEach(({ id, inputDetails }) => {
        const { defVal, dropDown } = inputDetails;
        if (Array.isArray(defVal)) {
          defVal.forEach((val, index) => {
            const newIndice = `_ind${index + 1}`;

            if (!newArrayIndices.some(({ indice }) => indice === newIndice)) {
              newArrayIndices.push({
                inputs,
                indice: newIndice,
              });
            }
            // Set each defVal item
            setValue(`${id}${newIndice}`, val);
          });
        } else {
          if (!newArrayIndices.some(({ indice }) => indice === nextIndice)) {
            newArrayIndices.push({
              inputs,
              indice: nextIndice,
            });
          }
          if (defVal) {
            setValue(`${id}${nextIndice}`, defVal);
          } else if (dropDown) {
            const options = parseDropdownOptions(
              `${id}${nextIndice}`,
              dropDown,
              mainScriptHeaderConfig,
              form
            );
            setValue(`${id}${nextIndice}`, options[0]?.value);
          } else {
            setValue(`${id}${nextIndice}`, ""); // Set empty string if no defVal or dropDown
          }
        }
      });
    } else {
      // If there are added inputs before
      newArrayIndices = [
        ...arrayIndices,
        {
          inputs,
          indice: nextIndice,
        },
      ];
      inputs.forEach(({ id, inputDetails }) => {
        const { defVal, dropDown } = inputDetails;
        if (defVal) {
          // If defVal is an array, set empty string, if not set the defVal
          setValue(`${id}${nextIndice}`, Array.isArray(defVal) ? "" : defVal);
        } else if (dropDown) {
          const options = parseDropdownOptions(
            `${id}${nextIndice}`,
            dropDown,
            mainScriptHeaderConfig,
            form
          );
          setValue(`${id}${nextIndice}`, options[0]?.value);
        } else {
          setValue(`${id}${nextIndice}`, ""); // Set empty string if no defVal or dropDown
        }
      });
    }
    setArrayIndices(newArrayIndices);
  };

  const handleDeleteInput = (indexToDelete: number) => {
    const removedIndice = arrayIndices[indexToDelete].indice;

    // Unregister the corresponding values from the form
    inputs.forEach(({ id }) => {
      unregister(`${id}${removedIndice}`);
    });

    // Remove the indice from arrayIndices
    setArrayIndices(arrayIndices.filter((_, index) => index !== indexToDelete));
  };

  return (
    <div className="mb-5">
      {title && <PageHeading title={title} size={HeadingSize.XSmall} />}
      {isArray ? (
        arrayIndices.map(({ indice }, indiceIndex) => {
          const rowHasValue = inputs.some(
            input => watch(`${input.id}${indice}`) !== undefined
          ); // This is used to determine unregistered inputs

          return rowHasValue ? (
            <div className="flex gap-4" key={indice}>
              {inputs.map((input, index) => {
                const updatedId = `${input.id}${indice}`;
                const updatedInputId = `${input.inputId}${indice}`;
                return (
                  <div
                    key={index}
                    className={classNames(
                      shouldDisplayInput(
                        updatedId,
                        updatedInputId,
                        style?.[0]?.condi || [],
                        mainScriptHeaderConfig,
                        form
                      )
                        ? "flex-1"
                        : "hidden"
                    )}>
                    {renderInputOptions(
                      { ...input, id: updatedId, inputId: updatedInputId },
                      index,
                      style
                    )}
                  </div>
                );
              })}
              {/* Only show the delete button if at least one input in the row has a value */}
              {!readOnly && rowHasValue && (
                <button
                  className="mt-8 inline-flex h-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline-red-600"
                  onClick={() => handleDeleteInput(indiceIndex)}>
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          ) : null; // If no values exist, don't render the row
        })
      ) : (
        <div className="flex gap-4">
          {inputs.map((input, index) => (
            <div
              key={index}
              className={classNames(
                shouldDisplayInput(
                  input.id,
                  input.inputId,
                  style?.[0]?.condi || [],
                  mainScriptHeaderConfig,
                  form
                )
                  ? "flex-1"
                  : "hidden"
              )}>
              {renderInputOptions(input, index, style)}
            </div>
          ))}
        </div>
      )}
      {isArray && !readOnly && (
        <button
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={handleAddInput}>
          Add
        </button>
      )}
    </div>
  );
};

export default HorizontalInputsArray;
